import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Line } from "@react-three/drei";
import { observer } from "mobx-react-lite";
import * as THREE from "three";
import {
  BookShelfConfig,
  BookShelfType,
  zIndexEpsilon,
} from "../../types/floorplannerTypes";
import {
  normalizeBookShelfConfig,
} from "../../features/floorplan3d/symbols/livingRoom/bookShelfConfig";
import { resolveBookShelfLayout } from "../../features/floorplan3d/symbols/livingRoom/bookShelfLayout";
import { CANVAS_ACCENT_COLOR } from "../../constants/brandColors";
import { editorStore } from "../../store/editorStore";
import { FloorplannerStoreContext } from "../../store/floorplannerStore";
import DraggableObject from "./DraggableObject";
import { BoundingBox, SelectableSymbol } from "./SelectableSymbol";
import { resolveFillOpacity } from "../../utils/fillOpacity";

type BookShelfSymbolProps = {
  bookShelf: BookShelfType;
  onDragStart: (symbol: BookShelfType, offset: [number, number]) => void;
  onDrag: (newPosition: [number, number]) => void;
  onDragEnd: (endPosition: [number, number]) => void;
};

const rectanglePoints = (
  width: number,
  height: number,
): Array<[number, number, number]> => [
  [-width / 2, -height / 2, 0.003],
  [width / 2, -height / 2, 0.003],
  [width / 2, height / 2, 0.003],
  [-width / 2, height / 2, 0.003],
  [-width / 2, -height / 2, 0.003],
];

/**
 * Floor-plan bookshelf: footprint rectangle with optional side frame / back
 * edge and light interior divider lines suggesting bays.
 */
const BookShelfPlan: React.FC<{
  width: number;
  depth: number;
  hasFrame: boolean;
  hasBackPanel: boolean;
  fill: THREE.ColorRepresentation;
  stroke: THREE.ColorRepresentation;
  lineWidth: number;
  fillOpacity: number;
}> = ({
  width,
  depth,
  hasFrame,
  hasBackPanel,
  fill,
  stroke,
  lineWidth,
  fillOpacity,
}) => {
  const backDepth = Math.min(Math.max(depth * 0.14, 0.02), 0.045);
  const sideWidth = Math.min(Math.max(width * 0.04, 0.015), 0.03);
  const dividerCount = 3;
  const dividers = useMemo(() => {
    const inset = hasFrame ? sideWidth : 0;
    const usableWidth = Math.max(width - 2 * inset, 0.001);
    const step = usableWidth / (dividerCount + 1);
    return Array.from({ length: dividerCount }, (_, index) => {
      const x = -usableWidth / 2 + step * (index + 1);
      return [
        [x, -depth / 2 + (hasBackPanel ? backDepth : 0), 0.004],
        [x, depth / 2, 0.004],
      ] as Array<[number, number, number]>;
    });
  }, [backDepth, depth, hasBackPanel, hasFrame, sideWidth, width]);
  const panelOpacity = Math.min(fillOpacity, 0.85);

  return (
    <group>
      <mesh position={[0, 0, 0.001]}>
        <planeGeometry args={[Math.max(width, 0.001), Math.max(depth, 0.001)]} />
        <meshBasicMaterial
          color={fill}
          side={THREE.DoubleSide}
          opacity={fillOpacity}
          transparent={fillOpacity < 1}
          depthWrite={fillOpacity >= 1}
        />
      </mesh>
      <Line
        points={rectanglePoints(width, depth)}
        color={stroke}
        lineWidth={lineWidth}
        renderOrder={2}
      />
      {hasFrame && (
        <>
          <mesh position={[-width / 2 + sideWidth / 2, 0, 0.002]}>
            <planeGeometry
              args={[Math.max(sideWidth, 0.001), Math.max(depth, 0.001)]}
            />
            <meshBasicMaterial
              color={stroke}
              side={THREE.DoubleSide}
              opacity={panelOpacity}
              transparent
              depthWrite={false}
            />
          </mesh>
          <mesh position={[width / 2 - sideWidth / 2, 0, 0.002]}>
            <planeGeometry
              args={[Math.max(sideWidth, 0.001), Math.max(depth, 0.001)]}
            />
            <meshBasicMaterial
              color={stroke}
              side={THREE.DoubleSide}
              opacity={panelOpacity}
              transparent
              depthWrite={false}
            />
          </mesh>
        </>
      )}
      {hasBackPanel && (
        <mesh position={[0, depth / 2 - backDepth / 2, 0.002]}>
          <planeGeometry args={[Math.max(width, 0.001), Math.max(backDepth, 0.001)]} />
          <meshBasicMaterial
            color={stroke}
            side={THREE.DoubleSide}
            opacity={panelOpacity}
            transparent
            depthWrite={false}
          />
        </mesh>
      )}
      {dividers.map((points, index) => (
        <Line
          key={`divider-${index}`}
          points={points}
          color={stroke}
          lineWidth={Math.max(lineWidth * 0.6, 0.5)}
          renderOrder={2}
        />
      ))}
    </group>
  );
};

const BookShelfSymbol: React.FC<BookShelfSymbolProps> = observer(
  ({ bookShelf, onDragStart, onDrag, onDragEnd }) => {
    const floorplannerStore = React.useContext(FloorplannerStoreContext);
    const groupRef = useRef<THREE.Group>(null);
    const [localConfig, setLocalConfig] = useState<BookShelfConfig>(() =>
      normalizeBookShelfConfig(bookShelf.bookShelf),
    );
    const configRef = useRef(localConfig);
    const [localPosition, setLocalPosition] = useState<[number, number]>([
      bookShelf.position.x,
      bookShelf.position.y,
    ]);
    const [isResizing, setIsResizing] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const layout = useMemo(
      () => resolveBookShelfLayout(localConfig),
      [localConfig],
    );
    const lineWidth =
      bookShelf.lineWeight ?? floorplannerStore.symbolLineWeight;
    const lineColor = bookShelf.lineColor ?? floorplannerStore.lineColor;
    const activeLineColor =
      isHovered || bookShelf.selected ? CANVAS_ACCENT_COLOR : lineColor;
    const fillColor =
      bookShelf.fillColor ?? floorplannerStore.fillColor ?? 0xffffff;
    const fillOpacity = resolveFillOpacity(bookShelf);

    const calculateBoundingBox = useCallback((): BoundingBox => {
      const { width, depth } = layout.bounds;
      return {
        topLeft: [-width / 2, depth / 2],
        topRight: [width / 2, depth / 2],
        bottomLeft: [-width / 2, -depth / 2],
        bottomRight: [width / 2, -depth / 2],
        width,
        height: depth,
        depth: 0,
      };
    }, [layout.bounds]);

    useEffect(() => {
      editorStore.updateGroupRef(bookShelf.id, groupRef.current);
    }, [bookShelf.id]);

    useEffect(() => {
      if (isResizing) return;
      const next = normalizeBookShelfConfig(bookShelf.bookShelf);
      configRef.current = next;
      setLocalConfig(next);
      setLocalPosition([bookShelf.position.x, bookShelf.position.y]);
    }, [
      isResizing,
      bookShelf.position.x,
      bookShelf.position.y,
      bookShelf.bookShelf,
    ]);

    return (
      <group position={[0, 0, bookShelf.zIndex * zIndexEpsilon]}>
        <DraggableObject
          position={localPosition}
          onDragStart={(offset) => {
            setIsDragging(true);
            onDragStart(bookShelf, offset);
          }}
          onDrag={onDrag}
          onDragEnd={(endPosition) => {
            onDragEnd(endPosition);
            setIsDragging(false);
          }}
          selectable
          attachmentId={bookShelf.id}
          attachmentType="noAttachments"
          symbol={bookShelf}
        >
          <SelectableSymbol
            symbol={bookShelf}
            handleSize={floorplannerStore.symbolHandleSize}
            calculateBoundingBox={calculateBoundingBox}
            onResizeStart={() => setIsResizing(true)}
            onResize={(newWidth, newDepth, _bounds, _handle, newPosition) => {
              const next = normalizeBookShelfConfig({
                ...localConfig,
                width: newWidth,
                depth: newDepth,
              });
              configRef.current = next;
              setLocalConfig(next);
              if (newPosition) setLocalPosition(newPosition);
            }}
            onResizeEnd={(_event, finalResize) => {
              setIsResizing(false);
              floorplannerStore.updateSymbolProperty(
                bookShelf.id,
                "bookShelf",
                configRef.current,
              );
              floorplannerStore.updateSymbolProperty(bookShelf.id, "threeD", {
                ...bookShelf.threeD,
                height: configRef.current.height,
                dimensions: {
                  ...bookShelf.threeD?.dimensions,
                  width: configRef.current.width,
                  depth: configRef.current.depth,
                  height: configRef.current.height,
                },
              });
              if (finalResize?.position) {
                floorplannerStore.updateSymbolProperty(
                  bookShelf.id,
                  "position",
                  finalResize.position,
                );
              }
            }}
            center={[0, 0]}
            rotation={bookShelf.rotation}
            drawHandles
            showMiddleHandles
            isDragging={isDragging}
            onPointerOver={() => setIsHovered(true)}
            onPointerOut={() => setIsHovered(false)}
          >
            <group
              ref={groupRef}
              rotation={[0, 0, bookShelf.rotation ?? 0]}
              onPointerEnter={() => {
                if (
                  document.body.style.cursor !== "grabbing" &&
                  document.body.style.cursor !== "move"
                ) {
                  document.body.classList.add("cursor-vector");
                }
              }}
              onPointerLeave={() =>
                document.body.classList.remove("cursor-vector")
              }
            >
              <BookShelfPlan
                width={layout.bounds.width}
                depth={layout.bounds.depth}
                hasFrame={layout.config.hasFrame}
                hasBackPanel={layout.config.hasBackPanel}
                fill={fillColor}
                stroke={activeLineColor}
                lineWidth={lineWidth}
                fillOpacity={fillOpacity}
              />
            </group>
          </SelectableSymbol>
        </DraggableObject>
      </group>
    );
  },
);

export default BookShelfSymbol;
